import { useEffect, useState } from 'react';
import { useRecordContext, useRefresh, RaRecord } from 'react-admin';
import { Box, Button, TextField, Typography } from '@mui/material';
import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { useHasPermission } from '../PermissionGate';
import { fmtDate } from '../../shared/Panel';

/**
 * ADR wpisywany ręcznie — karta pojazdu i kierowcy (07.10.2026).
 *
 * Odpowiedzi Marathonu 07.10.2026: ADR wyklucza przypisanie pojazdu. Ani
 * TMS, ani telematyka tej danej nie mają, więc wpisuje ją człowiek (decyzja
 * użytkownika). „Nie” wyklucza auto ze zleceń ADR w rekomendacjach i blokuje
 * obsadzenie; „nie wpisano” daje tylko ostrzeżenie. Zmiana idzie do
 * dziennika działań (API: `PUT /cars/:id/adr`, `PUT /drivers/:id/adr`).
 */

export type AdrResource = 'cars' | 'drivers';

export interface AdrState {
  adr: boolean | null;
  adrSetBy: string | null;
  adrSetAt: string | null;
}

type Choice = 'true' | 'false' | '';

export const adrLabel = (v: boolean | null | undefined): string =>
  v === true ? 'tak' : v === false ? 'nie' : 'nie wpisano';

const toChoice = (v: boolean | null): Choice => (v === true ? 'true' : v === false ? 'false' : '');
const fromChoice = (c: Choice): boolean | null => (c === 'true' ? true : c === 'false' ? false : null);

const stateOf = (r: Record<string, unknown> | undefined): AdrState => ({
  adr: typeof r?.adr === 'boolean' ? r.adr : null,
  adrSetBy: typeof r?.adrSetBy === 'string' ? r.adrSetBy : null,
  adrSetAt: typeof r?.adrSetAt === 'string' ? r.adrSetAt : null,
});

export async function saveManualAdr(
  resource: AdrResource,
  id: number | string,
  adr: boolean | null,
  reason: string,
): Promise<AdrState> {
  const { data } = await axiosNest.put(`/${resource}/${id}/adr`, {
    adr,
    reason: reason.trim() === '' ? null : reason.trim(),
  });
  return stateOf(data as Record<string, unknown>);
}

/** Kto i kiedy wpisał — „wpisał jan.kowalski, 07.10.2026, 13:05”. */
export const adrSource = (s: AdrState): string =>
  s.adrSetAt
    ? `wpisał ${s.adrSetBy ?? 'nieznany użytkownik'}, ${fmtDate(s.adrSetAt)}`
    : 'TMS i telematyka nie podają ADR — wpis ręczny';

export const ManualAdr = ({ resource }: { resource: AdrResource }) => {
  const r = useRecordContext<RaRecord & Record<string, unknown>>();
  const refresh = useRefresh();
  // Zmiana danych — gość tylko ogląda (`CHANGE_DATA`, egzekwuje API).
  const canEdit = useHasPermission('CHANGE_DATA');
  const [state, setState] = useState<AdrState>(() => stateOf(r));
  const [choice, setChoice] = useState<Choice>(() => toChoice(stateOf(r).adr));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const s = stateOf(r);
    setState(s);
    setChoice(toChoice(s.adr));
  }, [r]);

  if (!r) return null;
  const changed = fromChoice(choice) !== state.adr;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveManualAdr(resource, r.id, fromChoice(choice), reason);
      setState(saved);
      setChoice(toChoice(saved.adr));
      setReason('');
      refresh();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="manual-adr">
      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }} data-testid="adr-value">
        {adrLabel(state.adr)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {adrSource(state)}
      </Typography>
      {canEdit ? (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            select
            size="small"
            label="ADR"
            value={choice}
            disabled={saving}
            onChange={(e) => setChoice(e.target.value as Choice)}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          >
            <option value="">nie wpisano</option>
            <option value="true">tak</option>
            <option value="false">nie</option>
          </TextField>
          <TextField
            size="small"
            label="Powód (opcjonalnie)"
            value={reason}
            disabled={saving}
            onChange={(e) => setReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Button variant="outlined" size="small" disabled={!changed || saving} onClick={() => void save()}>
            Zapisz
          </Button>
        </Box>
      ) : null}
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          „Nie” wyklucza pojazd ze zleceń z ADR i blokuje obsadzenie. „Nie wpisano” daje w rekomendacjach tylko
          ostrzeżenie.
        </Typography>
      )}
    </Box>
  );
};
